import { Decoder, Encoder, Tag } from 'cbor-x'

/**
 * CBOR wire format for `tccc_card@1`. Mirrors the `x-cborKey` and
 * `x-cborEncoding` annotations in `schemas/tccc_card.v1.json`. The schema
 * file is the source of truth — bump `tccc_card@N` and add a new mapping
 * here when the wire format changes.
 *
 * Envelope shape: `[schemaName:tstr, version:uint, body:map]`. The body map
 * uses integer keys per the mapping; date-time fields are CBOR tag 1
 * (epoch seconds).
 */

type Mapping =
  | { kind: 'leaf' }
  | { kind: 'date' }
  | { kind: 'object'; props: Record<string, { key: number; map: Mapping }> }
  | { kind: 'array'; item: Mapping }

const LEAF: Mapping = { kind: 'leaf' }
const DATE: Mapping = { kind: 'date' }

const POINT_LIST_V1: Mapping = {
  kind: 'array',
  item: {
    kind: 'object',
    props: {
      x: { key: 1, map: LEAF },
      y: { key: 2, map: LEAF },
    },
  },
}

/** v2: flat interleaved integer array [x1, y1, x2, y2, ...] */
const POINT_LIST_V2: Mapping = { kind: 'array', item: LEAF }

const INJURY_LIST_V1: Mapping = {
  kind: 'array',
  item: {
    kind: 'object',
    props: {
      type: { key: 1, map: LEAF },
      points: { key: 2, map: POINT_LIST_V1 },
    },
  },
}

const INJURY_LIST_V2: Mapping = {
  kind: 'array',
  item: {
    kind: 'object',
    props: {
      type: { key: 1, map: LEAF },
      points: { key: 2, map: POINT_LIST_V2 },
    },
  },
}

const MEDICATION_LIST: Mapping = {
  kind: 'array',
  item: {
    kind: 'object',
    props: {
      name: { key: 1, map: LEAF },
      form: { key: 2, map: LEAF },
      dosage: { key: 3, map: LEAF },
      time: { key: 4, map: DATE },
    },
  },
}

const TCCC_CARD_V1: Mapping = {
  kind: 'object',
  props: {
    capturedAt: { key: 1, map: DATE },
    demographics: {
      key: 2,
      map: {
        kind: 'object',
        props: {
          battleRoster: { key: 1, map: LEAF },
          age: { key: 2, map: LEAF },
          height: { key: 3, map: LEAF },
          weight: { key: 4, map: LEAF },
          gender: { key: 5, map: LEAF },
          bloodType: { key: 6, map: LEAF },
          allergies: { key: 7, map: { kind: 'array', item: LEAF } },
          evacPriority: { key: 8, map: LEAF },
          lastName: { key: 9, map: LEAF },
          patientId: { key: 10, map: LEAF },
        },
      },
    },
    assessment: {
      key: 3,
      map: {
        kind: 'object',
        props: {
          avpu: { key: 1, map: LEAF },
          pain: { key: 2, map: LEAF },
        },
      },
    },
    injuriesFront: { key: 4, map: INJURY_LIST_V1 },
    injuriesBack: { key: 5, map: INJURY_LIST_V1 },
    tourniquets: {
      key: 6,
      map: {
        kind: 'array',
        item: {
          kind: 'object',
          props: {
            x: { key: 1, map: LEAF },
            y: { key: 2, map: LEAF },
            type: { key: 3, map: LEAF },
            createdAt: { key: 4, map: DATE },
            active: { key: 5, map: LEAF },
            updated: { key: 6, map: LEAF },
          },
        },
      },
    },
    treatments: {
      key: 7,
      map: {
        kind: 'object',
        props: {
          tq: { key: 1, map: { kind: 'array', item: LEAF } },
          dressing: { key: 2, map: { kind: 'array', item: LEAF } },
          airway: { key: 3, map: { kind: 'array', item: LEAF } },
          breathing: { key: 4, map: { kind: 'array', item: LEAF } },
          otherTreatments: { key: 5, map: { kind: 'array', item: LEAF } },
        },
      },
    },
    medications: {
      key: 8,
      map: {
        kind: 'object',
        props: {
          analgesics: { key: 1, map: MEDICATION_LIST },
          antibiotics: { key: 2, map: MEDICATION_LIST },
          ivFluids: { key: 3, map: MEDICATION_LIST },
          plasmaFluids: { key: 4, map: MEDICATION_LIST },
          txaFluids: { key: 5, map: MEDICATION_LIST },
          bloodFluids: { key: 6, map: MEDICATION_LIST },
        },
      },
    },
    vitalSigns: {
      key: 9,
      map: {
        kind: 'array',
        item: {
          kind: 'object',
          props: {
            time: { key: 1, map: DATE },
            pulse: { key: 2, map: LEAF },
            systolicBP: { key: 3, map: LEAF },
            diastolicBP: { key: 4, map: LEAF },
            respirationRate: { key: 5, map: LEAF },
            oxygenSaturation: { key: 6, map: LEAF },
            bloodLossDynamics: { key: 7, map: LEAF },
            shockIndex: { key: 8, map: LEAF },
          },
        },
      },
    },
    responder: {
      key: 10,
      map: {
        kind: 'object',
        props: {
          name: { key: 1, map: LEAF },
          battleRoster: { key: 2, map: LEAF },
        },
      },
    },
    notes: { key: 11, map: LEAF },
  },
}

/** v2 is identical to v1 except injury points use flat interleaved arrays. */
const v1Props = (TCCC_CARD_V1 as { props: Record<string, { key: number; map: Mapping }> }).props
const TCCC_CARD_V2: Mapping = {
  kind: 'object',
  props: {
    ...v1Props,
    injuriesFront: { key: 4, map: INJURY_LIST_V2 },
    injuriesBack: { key: 5, map: INJURY_LIST_V2 },
  },
}

const MAPPINGS: Record<string, Record<number, Mapping>> = {
  tccc_card: { 1: TCCC_CARD_V1, 2: TCCC_CARD_V2 },
}

function lookupMapping(name: string, version: number): Mapping {
  const versions = MAPPINGS[name]
  if (!versions) throw new Error(`Unknown CBOR schema name: ${name}`)
  const mapping = versions[version]
  if (!mapping) throw new Error(`Unsupported CBOR schema version: ${name}@${version}`)
  return mapping
}

// `mapsAsObjects: false` keeps integer-keyed CBOR maps as JS Map instances
// instead of being coerced into objects with stringified keys. We need the
// integer keys intact to drive the schema-based remapping below.
const decoder = new Decoder({ mapsAsObjects: false, useTimestamp32: false })
const encoder = new Encoder({ mapsAsObjects: false, useTimestamp32: true })

function fromCbor(value: unknown, mapping: Mapping): unknown {
  switch (mapping.kind) {
    case 'leaf':
      return value
    case 'date':
      // cbor-x decodes CBOR tag 1 (epoch time) into a JS Date. Tablets that
      // emit tag 1 as a bare uint of seconds also land here as Date. Either
      // way, render as ISO so the rest of the app sees a string.
      if (value instanceof Date) return value.toISOString()
      if (typeof value === 'number') return new Date(Math.round(value * 1000)).toISOString()
      return value
    case 'object': {
      if (!(value instanceof Map)) return value
      const out: Record<string, unknown> = {}
      for (const [propName, { key, map }] of Object.entries(mapping.props)) {
        if (!value.has(key)) continue
        out[propName] = fromCbor(value.get(key), map)
      }
      return out
    }
    case 'array': {
      if (!Array.isArray(value)) return value
      return value.map((item) => fromCbor(item, mapping.item))
    }
  }
}

function toCbor(value: unknown, mapping: Mapping): unknown {
  switch (mapping.kind) {
    case 'leaf':
      return value
    case 'date':
      if (typeof value === 'string') {
        const ms = Date.parse(value)
        if (!Number.isNaN(ms)) return new Tag(Math.floor(ms / 1000), 1)
      }
      if (value instanceof Date) return new Tag(Math.floor(value.getTime() / 1000), 1)
      return value
    case 'object': {
      if (value === null || typeof value !== 'object') return value
      const src = value as Record<string, unknown>
      const out = new Map<number, unknown>()
      for (const [propName, { key, map }] of Object.entries(mapping.props)) {
        const v = src[propName]
        if (v === undefined) continue
        out.set(key, toCbor(v, map))
      }
      return out
    }
    case 'array': {
      if (!Array.isArray(value)) return value
      return value.map((item) => toCbor(item, mapping.item))
    }
  }
}

export interface CborEnvelope {
  schemaName: string
  version: number
  body: Record<string, unknown>
}

/**
 * Decode a CBOR-encoded TCCC envelope (the post-gunzip byte payload). The
 * input must be the 3-element array `[name, version, body-map]` per the
 * `x-cborEnvelope` shape in the schema.
 */
export function decodeCborEnvelope(bytes: Uint8Array): CborEnvelope {
  const decoded: unknown = decoder.decode(bytes)
  if (!Array.isArray(decoded) || decoded.length !== 3) {
    throw new Error('CBOR envelope must be a 3-element array [name, version, body]')
  }
  const tuple = decoded as [unknown, unknown, unknown]
  const [schemaName, version, body] = tuple
  if (typeof schemaName !== 'string') {
    throw new Error('CBOR envelope schema name must be a string')
  }
  if (typeof version !== 'number' || !Number.isInteger(version) || version <= 0) {
    throw new Error('CBOR envelope version must be a positive integer')
  }
  const mapping = lookupMapping(schemaName, version)
  const remapped = fromCbor(body, mapping)
  if (remapped === null || typeof remapped !== 'object' || Array.isArray(remapped)) {
    throw new Error('CBOR envelope body must decode to an object')
  }
  return { schemaName, version, body: remapped as Record<string, unknown> }
}

/**
 * Encode a JSON-shaped TCCC body back into the CBOR wire format. Used by
 * tests and for round-trip parity checks; the live encoder path stays JSON
 * for now since we lose envelope-only fields (tabletUid, patient.name) on
 * the trip through CBOR.
 */
export function encodeCborEnvelope(env: CborEnvelope): Uint8Array {
  const mapping = lookupMapping(env.schemaName, env.version)
  const body = toCbor(env.body, mapping)
  const out = encoder.encode([env.schemaName, env.version, body])
  return new Uint8Array(out.buffer, out.byteOffset, out.byteLength)
}
